import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtMoney, shortDate } from "../lib/format";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useAccounts } from "../lib/accounts";
import { Crown, Wallet } from "lucide-react";
import HelpButton from "../components/HelpButton";

export default function Forecast() {
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const { user } = useAuth();
  const { current, selectedId } = useAccounts();
  // Persist the chosen horizon so it survives navigation. "This week" is the
  // fastest to reason about → we default to it.
  const [horizon, setHorizon] = useState(
    () => localStorage.getItem("cb_forecast_horizon") || "week"
  );

  useEffect(() => {
    localStorage.setItem("cb_forecast_horizon", horizon);
  }, [horizon]);

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams();
        if (selectedId) params.set("account_id", selectedId);
        params.set("horizon", horizon);
        const { data } = await api.get(`/forecast?${params.toString()}`);
        setData(data);
      } catch (e) {
        if (e?.response?.status === 402) setLocked(true);
        else console.error("Forecast load failed:", e?.message);
      }
    })();
  }, [selectedId, horizon]);

  if (locked || (user && user.modules && user.modules.forecast === false)) {
    return (
      <div className="space-y-4" data-testid="forecast-locked">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-black">Forecast</h1>
          <HelpButton id="forecast" />
        </div>
        <div className="nb-card-lg p-8 text-center" style={{ background: "linear-gradient(135deg,#fdecc8,#d9d4f1)" }}>
          <Crown size={48} strokeWidth={2.5} className="mx-auto mb-3" />
          <div className="font-display font-black text-2xl mb-2">Paid feature</div>
          <div className="text-sm font-medium text-[#4a4a4a] mb-4">
            The 2-week forecast is part of SyncBook Pro. Ask your admin to upgrade your plan to unlock it.
          </div>
          <Link to="/" className="nb-btn nb-btn-dark inline-flex">
            Back home
          </Link>
        </div>
      </div>
    );
  }

  const series = (data?.series || []).map((s) => ({ date: s.date, balance: s.balance }));

  return (
    <div className="space-y-4" data-testid="forecast-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-black">Forecast</h1>
        <HelpButton id="forecast" />
      </div>

      {current && (
        <div
          className="nb-card p-2 flex items-center gap-2 text-sm font-medium"
          style={{ background: current?.color || "#fff" }}
          data-testid="forecast-account-hint"
        >
          <Wallet size={14} strokeWidth={3} />
          <span className="font-black">Forecasting</span>
          <span>{current.name}</span>
          <span className="text-[11px] text-[#4a4a4a] ml-auto">Switch account chips at the top to change</span>
        </div>
      )}

      {/* Horizon toggle — three chips styled like a segmented control. Users
          who think in weeks pick "This week"; users who think paycheck-to-
          paycheck pick "Pay period". "2 weeks" stays as the roomier fallback.
          Selection persists across visits via localStorage. */}
      <div
        data-testid="forecast-horizon-toggle"
        className="nb-card p-1 flex gap-1"
        role="tablist"
      >
        {[
          { id: "week", label: "This week" },
          { id: "pay_period", label: "Pay period" },
          { id: "14d", label: "2 weeks" },
        ].map((opt) => (
          <button
            key={opt.id}
            data-testid={`horizon-${opt.id}`}
            role="tab"
            aria-selected={horizon === opt.id}
            onClick={() => setHorizon(opt.id)}
            className={`flex-1 py-2 text-xs font-black uppercase tracking-wide rounded-md transition ${
              horizon === opt.id
                ? "bg-[#1a1a1a] text-white"
                : "hover:bg-[#f4f0eb]"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Fallback hint — server returns horizon_mode="pay_period_fallback"
          when the user picked "Pay period" but has no paycheck set up yet. */}
      {data?.horizon_mode === "pay_period_fallback" && (
        <div
          data-testid="pay-period-fallback-hint"
          className="nb-card p-3 text-xs font-bold bg-[#fdecc8] flex items-center gap-2"
        >
          <span>💡</span>
          <span className="flex-1">
            No paycheck scheduled yet — showing the next 2 weeks instead. Add one under{" "}
            <Link to="/recurring" className="underline">Repeats</Link> to lock this to your real pay-period.
          </span>
        </div>
      )}

      <div className="nb-card-lg p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="label">
              {horizon === "week"
                ? "End of this week"
                : horizon === "pay_period" && data?.horizon_mode !== "pay_period_fallback"
                ? `By next paycheck (${data?.horizon_date ? shortDate(data.horizon_date) : "…"})`
                : `In ${data?.horizon_days ?? 14} days`}
            </div>
            <div
              data-testid="projected-horizon"
              className={`font-display font-black text-4xl ${
                (data?.projected_horizon ?? data?.projected_90d ?? 0) < 0 ? "balance-neg" : ""
              }`}
            >
              {fmtMoney(data?.projected_horizon ?? data?.projected_90d ?? 0)}
            </div>
          </div>
          <div className="text-right">
            <div className="label">Lowest point</div>
            <div
              className={`font-display font-black text-xl ${
                (data?.min_balance ?? 0) < 0 ? "balance-neg" : ""
              }`}
            >
              {fmtMoney(data?.min_balance ?? 0)}
            </div>
          </div>
        </div>
        <div className="h-64">
          {series.length > 1 && (
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d9d4f1" stopOpacity={1} />
                    <stop offset="100%" stopColor="#d9d4f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  tick={{ fontWeight: 700, fontSize: 10, fill: "#4a4a4a" }}
                  interval={14}
                />
                <YAxis
                  tick={{ fontWeight: 700, fontSize: 10, fill: "#4a4a4a" }}
                  tickFormatter={(v) => `$${Math.round(v)}`}
                  width={50}
                />
                <ReferenceLine y={0} stroke="#c92a2a" strokeDasharray="4 4" strokeWidth={2} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "2px solid #1a1a1a",
                    borderRadius: 8,
                    fontWeight: 700,
                  }}
                  formatter={(v) => [fmtMoney(v), "Balance"]}
                  labelFormatter={shortDate}
                />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#1a1a1a"
                  strokeWidth={3}
                  fill="url(#g1)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <Link to="/recurring" data-testid="edit-recurring-link" className="nb-btn nb-btn-secondary w-full">
        Manage bills & paychecks
      </Link>

      <div className="nb-card p-5">
        <div className="label mb-2">Upcoming</div>
        {(data?.upcoming || []).length === 0 && (
          <div className="text-sm text-[#4a4a4a] font-medium py-3 text-center">
            No upcoming items in the next week.
          </div>
        )}
        {(data?.upcoming || []).map((e) => (
          <div
            key={`${e.date}-${e.name}`}
            className="flex items-center justify-between py-2 border-b-2 border-[#1a1a1a]/10 last:border-0"
          >
            <div>
              <div className="font-bold">{e.name}</div>
              <div className="text-xs text-[#4a4a4a] font-medium">{shortDate(e.date)}</div>
            </div>
            <div className={`font-display font-black text-lg ${e.delta >= 0 ? "balance-pos" : ""}`}>
              {e.delta >= 0 ? "+" : ""}
              {fmtMoney(e.delta)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
